import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";

import OnboardingRoute from "@/components/OnboardingRoute/OnboardingRoute";
import ProtectedRoute from "@/components/ProtectedRoute/ProtectedRoute";
import PublicRoute from "@/components/PublicRoute/PublicRoute";
import RequireOrganisationRoute from "@/components/RequireOrganisationRoute/RequireOrganisationRoute";
import VolunteerProtectedRoute from "@/components/VolunteerProtectedRoute/VolunteerProtectedRoute";
import VolunteerPublicRoute from "@/components/VolunteerPublicRoute/VolunteerPublicRoute";

import { getPagePath, PAGES } from "../../config/pages";
import type { AppLanguage } from "../../i18n/config";
import {
  detectPreferredLanguage,
  isSupportedLanguage,
} from "../../i18n/routing";
import useSyncLanguage from "../../i18n/useSyncLanguage";
import AppShell from "../AppShell";
import {
  Contracts,
  ContractTemplates,
  CookiePolicy,
  EventDetail,
  EventsList,
  FinanceSettings,
  ForgotPassword,
  GeneralSettings,
  Home,
  InvitationsSettings,
  Login,
  MembersSettings,
  NewEvent,
  NewVolunteer,
  NotFound,
  Onboarding,
  OrganisationInvitation,
  Pricing,
  Profile,
  PublicGuestList,
  ReferentialsSettings,
  Register,
  ResetPassword,
  SettingsLayout,
  TermsOfUses,
  VolunteerDashboard,
  VolunteerEventGuests,
  VolunteerForgotPassword,
  VolunteerLinkSettings,
  VolunteerLogin,
  VolunteerResetPassword,
  VolunteerSignup,
  VolunteersList,
} from "./lazyPages";

function LangRedirect() {
  const language = detectPreferredLanguage();

  return <Navigate to={getPagePath("main", language)} replace />;
}

function LocalizedApp() {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();

  const currentLanguage: AppLanguage | null = isSupportedLanguage(lang)
    ? lang
    : null;

  useSyncLanguage(currentLanguage);

  useEffect(() => {
    if (!currentLanguage) {
      navigate(PAGES.main, { replace: true });
    }
  }, [currentLanguage, navigate]);

  if (!currentLanguage) {
    return null;
  }

  return (
    <Routes>
      {/* Public marketing pages — standalone, rendered without the AppShell */}
      <Route index element={<Home />} />
      <Route path={PAGES.pricing} element={<Pricing />} />

      {/* Auth pages — no navbar */}
      <Route element={<PublicRoute />}>
        <Route path={PAGES.login} element={<Login />} />
        <Route path={PAGES.register} element={<Register />} />
        <Route path={PAGES.forgotPassword} element={<ForgotPassword />} />
        <Route path={PAGES.resetPassword} element={<ResetPassword />} />
      </Route>
      <Route
        path={PAGES.organisationInvitation}
        element={<OrganisationInvitation />}
      />
      <Route element={<VolunteerPublicRoute />}>
        <Route path={PAGES.volunteerLogin} element={<VolunteerLogin />} />
        <Route
          path={PAGES.volunteerForgotPassword}
          element={<VolunteerForgotPassword />}
        />
        <Route
          path={PAGES.volunteerResetPassword}
          element={<VolunteerResetPassword />}
        />
      </Route>
      <Route path={PAGES.volunteerSignup} element={<VolunteerSignup />} />
      <Route path={PAGES.publicGuestList} element={<PublicGuestList />} />
      <Route path={PAGES.cookiePolicy} element={<CookiePolicy />} />
      <Route path={PAGES.termsOfUse} element={<TermsOfUses />} />
      <Route element={<VolunteerProtectedRoute />}>
        <Route
          path={PAGES.volunteerDashboard}
          element={<VolunteerDashboard />}
        />
        <Route
          path={PAGES.volunteerEventGuests}
          element={<VolunteerEventGuests />}
        />
      </Route>

      {/* Onboarding — protected, no navbar, blocked if org already exists */}
      <Route element={<ProtectedRoute />}>
        <Route element={<OnboardingRoute />}>
          <Route path={PAGES.onboarding} element={<Onboarding />} />
        </Route>
      </Route>

      {/* App pages — full AppShell with navbar */}
      <Route
        element={
          <AppShell showLangSwitcher>
            <Outlet />
          </AppShell>
        }
      >
        <Route element={<ProtectedRoute />}>
          <Route element={<RequireOrganisationRoute />}>
            <Route path={PAGES.events} element={<EventsList />} />
            <Route path={PAGES.newEvent} element={<NewEvent />} />
            <Route path={PAGES.eventDetail} element={<EventDetail />} />
            <Route path={PAGES.contracts} element={<Contracts />} />
            <Route path={PAGES.profile} element={<Profile />} />
            <Route
              path={PAGES.settings}
              element={
                <Navigate
                  to={getPagePath("organisationSettings", currentLanguage)}
                  replace
                />
              }
            />
            <Route element={<SettingsLayout />}>
              <Route
                path={PAGES.organisationSettings}
                element={<GeneralSettings />}
              />
              <Route
                path={`${PAGES.organisationSettings}/:slug`}
                element={<GeneralSettings />}
              />
              <Route
                path={PAGES.settingsMembers}
                element={<MembersSettings />}
              />
              <Route
                path={PAGES.settingsInvitations}
                element={<InvitationsSettings />}
              />
              <Route
                path={PAGES.settingsVolunteerLink}
                element={<VolunteerLinkSettings />}
              />
              <Route
                path={PAGES.settingsReferentials}
                element={<ReferentialsSettings />}
              />
              <Route
                path={PAGES.settingsFinance}
                element={<FinanceSettings />}
              />
              <Route
                path={PAGES.settingsContractTemplates}
                element={<ContractTemplates />}
              />
            </Route>
            <Route path={PAGES.volunteers} element={<VolunteersList />} />
            <Route path={PAGES.newVolunteer} element={<NewVolunteer />} />
          </Route>
        </Route>
      </Route>

      {/* 404 — standalone page, rendered without the AppShell */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function I18nRouter() {
  return (
    <Routes>
      <Route path="/:lang/*" element={<LocalizedApp />} />
      <Route
        path={PAGES.organisationInvitation}
        element={<OrganisationInvitation />}
      />
      {/* Emailed password-reset links carry no language prefix. Static routes
          outrank "/:lang/*", so they render directly instead of being treated
          as an unknown language and bounced to the home page. */}
      <Route path={PAGES.resetPassword} element={<ResetPassword />} />
      <Route
        path={PAGES.volunteerResetPassword}
        element={<VolunteerResetPassword />}
      />
      <Route path={PAGES.main} element={<LangRedirect />} />
      <Route path="*" element={<LangRedirect />} />
    </Routes>
  );
}

export default I18nRouter;
